/**
 * Destinations possibles d'un dépôt depuis la page « Mes fichiers » : les
 * contacts (tuteur↔élève lié + mentorat académique) et, côté tuteur, ses
 * classes. Les règles de construction sont PURES (testées) ; les hooks ne
 * font que rassembler les listes déjà connues de l'app.
 *
 * Miroir UI de la RLS d'INSERT de `documents` (migration 0261) : un fichier
 * de classe ne se dépose que par le TUTEUR ; le serveur reste la garde ultime.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { DocumentTarget } from '@jeraisonne/documents';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useTutorClasses, useTutorStudents } from '@/lib/tutor/queries';
import { useAcademicMentees } from '@/lib/tutor/rendezvous';

export interface DocumentDestination {
  /** Clé stable (valeur du `<select>`). */
  key: string;
  label: string;
  group: 'contact' | 'class';
  target: DocumentTarget;
}

interface NamedId {
  id: string;
  name: string;
}

const byName = (a: DocumentDestination, b: DocumentDestination) =>
  a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });

/** Contacts dédupliqués par id (un alternant lié ET rattaché ne compte qu'une fois), puis classes. */
export function buildDestinations(input: {
  contacts: readonly NamedId[];
  classes?: readonly NamedId[];
}): DocumentDestination[] {
  const contacts = new Map<string, DocumentDestination>();
  for (const c of input.contacts) {
    if (!c.id || contacts.has(c.id)) continue;
    contacts.set(c.id, {
      key: `contact:${c.id}`,
      label: c.name || 'Contact',
      group: 'contact',
      target: { kind: 'contact', peerId: c.id },
    });
  }
  const classes: DocumentDestination[] = (input.classes ?? []).map((c) => ({
    key: `class:${c.id}`,
    label: c.name,
    group: 'class',
    target: { kind: 'class', classId: c.id },
  }));
  return [...[...contacts.values()].sort(byName), ...classes.sort(byName)];
}

/** Tuteur : élèves liés ACTIFS, étudiants rattachés (mentorat), classes non archivées. */
export function useTutorDocumentDestinations(): {
  destinations: DocumentDestination[];
  isLoading: boolean;
} {
  const { user } = useAuth();
  const tutorId = user?.id ?? '';
  const students = useTutorStudents(tutorId);
  const mentees = useAcademicMentees(user?.id);
  const classes = useTutorClasses(tutorId);

  const destinations = buildDestinations({
    contacts: [
      ...(students.data ?? [])
        .filter((s) => s.status === 'active')
        .map((s) => ({ id: s.student_id, name: s.display_name })),
      ...(mentees.data ?? []).map((m) => ({ id: m.studentId, name: m.displayName })),
    ],
    classes: (classes.data ?? [])
      .filter((c) => !c.archived_at)
      .map((c) => ({ id: c.id, name: c.name })),
  });

  return {
    destinations,
    isLoading: students.isLoading || mentees.isLoading || classes.isLoading,
  };
}

/** Élève : tuteurs liés ACTIFS + tuteur académique. Jamais de classe (dépôt réservé au tuteur). */
export function useStudentDocumentDestinations(): {
  destinations: DocumentDestination[];
  isLoading: boolean;
} {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['document-destinations', 'student', user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<NamedId[]> => {
      const db = supabase as unknown as SupabaseClient;
      const [links, academic] = await Promise.all([
        db
          .from('tutor_links')
          .select('profiles!tutor_links_tutor_id_fkey ( id, display_name:visible_name )')
          .eq('student_id', user?.id as string)
          .eq('status', 'active'),
        // student_profiles a 2 FK vers profiles (0237) → embed désambiguïsé.
        db
          .from('student_profiles')
          .select(
            'academic_tutor_id, tutor:profiles!student_profiles_academic_tutor_id_fkey ( display_name:visible_name )',
          )
          .eq('id', user?.id as string)
          .maybeSingle(),
      ]);
      if (links.error) throw new Error(links.error.message);

      const contacts: NamedId[] = [];
      for (const row of (links.data ?? []) as unknown as Array<{
        profiles: { id: string; display_name: string } | null;
      }>) {
        if (row.profiles) contacts.push({ id: row.profiles.id, name: row.profiles.display_name });
      }
      const mentor = academic.data as {
        academic_tutor_id: string | null;
        tutor: { display_name: string } | null;
      } | null;
      if (mentor?.academic_tutor_id) {
        contacts.push({
          id: mentor.academic_tutor_id,
          name: mentor.tutor?.display_name ?? 'Tuteur académique',
        });
      }
      return contacts;
    },
  });

  return {
    destinations: buildDestinations({ contacts: query.data ?? [] }),
    isLoading: query.isLoading,
  };
}
